/**
 * <user-settings-view> — wraps the server-rendered settings.html view and
 * keeps form inputs + status badges live across the same user's other
 * tabs / browsers / devices.
 *
 * Listens for `user_profile_updated` on `luboo:user:{userId}` and patches
 * the existing DOM by id, with a critical UX rule: NEVER overwrite a
 * field the user is currently focused on — clobbering an in-progress
 * edit on the same screen is the worst possible behavior. Drops the
 * patch for focused fields and lets the user save their version.
 *
 * Used by user / account / property shells (registered via
 * register-shared-elements.js) because settings.html is shared.
 */

import { setupChannel, destroyChannel } from '../mixins/data-channel.mixin.js';

class UserSettingsView extends HTMLElement {
  setupChannel = setupChannel;
  destroyChannel = destroyChannel;

  _messageMap = {
    user_profile_updated: this._onUserProfileUpdated,
  };

  connectedCallback() {
    const userId = window.luboo?.data?._userId;
    if (!userId) return;
    this.setupChannel(`luboo:user:${userId}`);
  }

  disconnectedCallback() {
    this.destroyChannel();
  }

  _onUserProfileUpdated(user) {
    if (!user) return;
    // Form fields — skip clobbering if focused right now, since this view
    // is also the edit surface and a remote update arriving mid-typing
    // would erase the user's in-flight keystrokes.
    const setInput = (id, value) => {
      const el = this.querySelector(`#${id}`);
      if (!el) return;
      if (document.activeElement === el) return;
      if (value == null) return;
      el.value = value;
    };
    setInput('settings-first-name', user.first_name);
    setInput('settings-last-name', user.last_name);
    setInput('settings-phone', user.phone);
    setInput('settings-timezone', user.timezone);
    setInput('settings-locale', user.locale);
  }
}

export { UserSettingsView };
